import {LitElement, html, css} from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { videoPlayerCss } from '../components/slds-system';
import { connect } from 'pwa-helpers/connect-mixin.js';
import store from '../src/store';
import "./post-actions.js";
import "./post-save.js";
import { getTimePassed, debounce, getVideoOrImage } from '../src/apiCommon.js';
import { SldsBaseElement } from '../components/slds-base-element.js';

@customElement('single-post')
class SinglePost extends connect(store)(SldsBaseElement) {
  @property({type: Object}) item = null;
  @property({type: String}) postOpen = '';
  @property({type: String}) theme = 'light';
  @property({type: Array}) categories = [];
  @property({type: Boolean}) isVisible = false;
  @property({type: Boolean}) fullyOutOfView = false;
  @property({type: Boolean}) mobileMode = false;
  intersectionRatio = 0;
  isPlaying = false;
  
  constructor() {
    super();
    // Main observer for initial visibility
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.isVisible = true;
          this.fullyOutOfView = false;
          
          // Create second observer when post comes into view
          this.videoObserver = new IntersectionObserver(
            ([videoEntry]) => {
              if (videoEntry.isIntersecting && !this.fullyOutOfView) {
                this.intersectionRatio = videoEntry.intersectionRatio;
                this.dispatchEvent(new CustomEvent('post-visible', {
                  detail: {
                    id: this.item?.url,
                    intersectionRatio: this.intersectionRatio
                  }
                }));
              }
            },
            { 
              root: null,
              threshold: this.mobileMode ? 0.5 : 0.8 // Lower threshold on mobile
            }
          );
          
          this.videoObserver.observe(this);

        } else if (!this.fullyOutOfView) {
          this.fullyOutOfView = true;
          
          // Disconnect video observer when main element leaves view
          if (this.videoObserver) {
            this.videoObserver.disconnect();
          }

          debounce(() => {
            if (this.fullyOutOfView) this.isVisible = false;
          }, 200);
        }
      },
      { 
        root: null, 
        rootMargin: this.mobileMode ? '200px' : '800px',
        threshold: this.mobileMode ? 0.3 : 0.5
      }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
    this.observer.unobserve(this);
    super.disconnectedCallback();
  }

  stateChanged(state) {
    // console.log("State changed", state);
    if (!this.fullyOutOfView && state.playVideo) {
      if (state.playVideo.videoId === this.item?.url) {
        const video = this.shadowRoot.querySelector('video');
        if (video) {
          try {
            if (!video.paused) {
              return;
            }
            
            // Always mute video initially to allow autoplay
            video.muted = true;
            video.dispatchEvent(new Event('volumechange'));
            
            // Play the video
            const playPromise = video.play();
            
            if (playPromise !== undefined) {
              playPromise.then(() => {
                // Video started playing successfully
                this.isPlaying = true;
                
                // After successful autoplay, update mute state based on preference
                if (!window.muteByDefault) {
                  video.muted = false;
                  video.dispatchEvent(new Event('volumechange')); 
                }
              }).catch(e => {
                console.error('Error playing video:', e);
              });
            }
          } catch (e) {
            console.error('Error playing video:', e);
          }
        }
      }
      else {
        this.item.autoPlay = false;
        if (this.isPlaying) {
          const video = this.shadowRoot.querySelector('video');
          if (video) {
            video.pause();
            this.isPlaying = false;
          }
        }
      }
    }
  }

  lookupCategory(id) {
    for (var i = 0; i < this.categories.length; i++) {
      if (this.categories[i].id === id) {
        return this.categories[i];
      }
    }
    return null;
  }

  valToStr(val) {
    if (val === undefined || val === null) {
      return '';
    }
    const formattedVal = (val / 1000).toFixed(1);
    if (val<1000) {
      return val;
    }
    else
    {
      return (formattedVal.split(".").pop()==="0"?(val / 1000).toFixed(0):formattedVal) + 'k';
    }
  }

  openDialog(e) {
    e.preventDefault();
    this.dispatchEvent(new CustomEvent('dialog-opened', {detail: {id: this.item?.url}}));
  }

  static styles = [
    super.styles,
    videoPlayerCss(),
    css`
      .slds-theme_warning.pro-badge {
        padding-top: 0;
        padding-bottom: 0;
        padding-left: 0.1rem;
        padding-right: 0.1rem;
        border-radius: 0.25rem;
        font-size: 10px;
      }
      .timeago {
        color: var(--text-color);
      }
      .slds-avatar {
        margin-top: -0.1rem;
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      .fade-in {
        opacity: 0;
        animation: fadeIn 0.3s ease forwards;
      }
      post-save {
        --background-color2: var(--background-color);
        --text-color2: var(--text-color);
      }
    `
  ];

  render() {
    return html`
      ${!this.isVisible ? html`<div style="height: 36rem;"></div>` : html`<div class="slds-m-bottom_large fade-in">
        <div class="slds-p-bottom_x-small ${this.mobileMode ? 'slds-p-left_large slds-p-right_large' : ''}">
          <span class="slds-text-heading_small">
            <img src="${this.lookupCategory(this.item?.channel)?.icon}" alt="${this.lookupCategory(this.item?.channel)?.title}" class="slds-avatar slds-avatar_small" />
            <a href="/interest/${this.item?.channel}">${this.lookupCategory(this.item?.channel)?.title}</a>
          </span>
          <span class="slds-m-left_x-small timeago">${getTimePassed(this.item?.created)}</span>
          <post-save .item="${this.item}" class="slds-float_right"></post-save>
        </div>
        <a @click="${this.openDialog}" href="/post/${this.item?.url}" class="slds-text-title_bold"><h2 class="slds-text-heading_medium ${this.mobileMode ? 'slds-p-left_large slds-p-right_large slds-m-bottom_small' : 'slds-m-bottom_x-small'}">${this.item?.title}</h2></a>
        <div class="${(this.postOpen===this.item?.url)?'slds-hidden':''}">${getVideoOrImage(this.item?.files, this.item?.title, this.item?.url, this.openDialog, this.item?.autoPlay)}</div>
        <post-actions .item="${this.item}" @dialog-opened="${this.openDialog}" commentEnabled="true" .mobileMode="${this.mobileMode}"></post-actions>
        <hr>
      </div>`}
    `;
  }
}